import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const StudioPublicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, login } = useAuth();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState(''); // 'sent', 'error'
  const [forgotLoading, setForgotLoading] = useState(false);

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const { data } = await api.get(`/widget/${slug}/courses`);
        setStudio(data.studio);
      } catch (err) {
        setError('Studio not found.');
      } finally {
        setLoading(false);
      }
    };
    fetchStudio();
  }, [slug]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studio?.name) {
      document.title = studio.name;
    }
    if (studio?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studio.branding.logo;
      document.head.appendChild(link);
    }
  }, [studio]);

  // If already logged in, redirect to appropriate dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'student' && user.studioId) {
        navigate('/student');
      } else if (user.role === 'teacher') {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSignIn = () => {
    login({ appState: { returnTo: `/s/${slug}` } });
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    setForgotStatus('');
    try {
      await api.post('/auth/reset-password', {
        email: forgotEmail,
        studioSlug: slug
      });
      setForgotStatus('sent');
    } catch (err) {
      setForgotStatus('error');
    } finally {
      setForgotLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  if (error || !studio) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Studio Not Found</h1>
          <p className="text-gray-500">The studio you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-gray-100 flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          {/* Studio Logo/Name */}
          <div className="text-center mb-8">
            {studio.branding?.logo ? (
              <img
                src={studio.branding.logo}
                alt={studio.name}
                className="h-20 mx-auto mb-4 object-contain"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-brand-600">
                  {studio.name?.charAt(0) || 'S'}
                </span>
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-900">{studio.name}</h1>
            {studio.location?.city && (
              <p className="text-sm text-gray-500 mt-1">
                {[studio.location.city, studio.location.state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>

          {/* Sign In Button */}
          <button
            onClick={handleSignIn}
            className="w-full bg-brand-500 text-white py-3 rounded-xl text-sm font-semibold hover:bg-brand-600 transition-colors"
          >
            Sign In
          </button>

          {/* Forgot Password */}
          {!showForgot ? (
            <div className="text-center mt-4">
              <button
                onClick={() => setShowForgot(true)}
                className="text-sm text-brand-500 hover:text-brand-600 font-medium"
              >
                Forgot your password?
              </button>
            </div>
          ) : (
            <div className="mt-4">
              {forgotStatus === 'sent' ? (
                <div className="text-center p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">Password reset email sent! Check your inbox.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-2">Enter your email to reset your password</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                    />
                    <button
                      onClick={handleForgotPassword}
                      disabled={forgotLoading || !forgotEmail}
                      className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                    >
                      {forgotLoading ? '...' : 'Send'}
                    </button>
                  </div>
                  {forgotStatus === 'error' && (
                    <p className="text-xs text-red-500 mt-1">Something went wrong. Try again.</p>
                  )}
                  <button
                    onClick={() => { setShowForgot(false); setForgotStatus(''); }}
                    className="text-xs text-gray-400 hover:text-gray-600 mt-2"
                  >
                    Back to Sign In
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by <a href="https://mydancehall.com" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-500">DanceHall</a>
        </p>
      </div>
    </div>
  );
};

export default StudioPublicPage;
